import React, { useState } from 'react';

import { FeedbackSnackbar, useSnackbar } from '~/components/core/Molecules/Snackbar';
import { MainCard } from '~/components/core/NewDesignSystem/Cards/MainCard/MainCard';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';

import SorIssueDetailsDialog from './components/SorIssueDetailsDialog';
import SorIssuesFilters from './components/SorIssuesFilters';
import SorIssuesTable from './components/SorIssuesTable';
import { useSorIssues } from './hooks/useSorIssues';
import TEXTS from './texts';
import type { SorIssue, SorReconcileReport, SorUser } from './types';

const getUserName = (users: SorUser[], userId: string) => users.find(({ id }) => id === userId)?.username ?? userId;

const hasWarning = ({ outcomes, errored, truncated }: SorReconcileReport) =>
  outcomes.unreachable > 0 || errored > 0 || truncated;

const describeReport = ({ outcomes, errored, truncated }: SorReconcileReport) =>
  [
    TEXTS.issues.reconciled(outcomes.completed, outcomes.failed, outcomes.retrying, outcomes.needs_attention),
    outcomes.unreachable > 0 ? TEXTS.issues.unreachable : null,
    truncated ? TEXTS.issues.truncated : null,
    errored > 0 ? TEXTS.issues.errored(errored) : null,
  ]
    .filter(Boolean)
    .join(' ');

const SorIssuesTab: React.FC = () => {
  // `users` leaves out removed users, whom `allUsers` keeps so that the attempts they requested still show their name.
  const { users, allUsers } = useSysconfig() as { users: SorUser[]; allUsers: SorUser[] };
  const {
    filters,
    updateFilters,
    clearFilters,
    operations,
    issues,
    statusCounts,
    pagination,
    isLoading,
    isError,
    reload,
    isReconciling,
    reconcile,
    resolveAttempt,
  } = useSorIssues();
  const [selectedIssue, setSelectedIssue] = useState<SorIssue | null>(null);
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  const userName = (userId: string) => getUserName(allUsers, userId);

  const handleReconcile = async () => {
    try {
      const report = await reconcile();
      showSnackbar(describeReport(report), hasWarning(report) ? 'warning' : 'success');
    } catch {
      // Reported by the hook.
    }
  };

  return (
    <MainCard type="elevated" title={TEXTS.tabs.issues}>
      <SorIssuesFilters
        filters={filters}
        operations={operations}
        statusCounts={statusCounts}
        isReconciling={isReconciling}
        onFiltersChange={updateFilters}
        onClearFilters={clearFilters}
        onReconcile={handleReconcile}
      />
      <SorIssuesTable
        issues={issues}
        isLoading={isLoading}
        isError={isError}
        pagination={pagination}
        userName={userName}
        onRefresh={reload}
        onIssueClick={setSelectedIssue}
      />
      {selectedIssue && (
        <SorIssueDetailsDialog
          issue={selectedIssue}
          replayUsers={users.filter(({ is_active }) => is_active)}
          userName={userName}
          onResolve={async (params) => setSelectedIssue(await resolveAttempt(selectedIssue.id, params))}
          onClose={() => setSelectedIssue(null)}
        />
      )}
      <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
    </MainCard>
  );
};

export default SorIssuesTab;
